<template>
  <div class="bg-white p-4 rounded-lg">
    <h1 class="font-semibold text-3xl">Settings</h1>
    <div class="overflow-x-auto">
      <nav class="my-4">
        <ul class="flex flex-nowrap border-b border-grey-light-200 whitespace-nowrap">
          <li
            v-for="tab in tabs"
            :key="tab.value"
            @click="setActiveTab(tab.value)"
            class="font-semibold py-2 px-4 text-sm cursor-pointer border-b-2 transition-colors duration-200"
            :class="[
              activeTab === tab.value
                ? 'border-brand-700 text-brand-700'
                : 'border-transparent text-grey-light-500 hover:border-brand-700 hover:text-brand-700',
            ]"
          >
            {{ tab.label }}
          </li>
        </ul>
      </nav>
    </div>

    <div class="mt-0">
      <div class="flex justify-center" v-if="isLoading">
        <LayupSpinner :size="10" />
      </div>
      <template v-else>
        <Tabs.OrganizationTab
          :settings="organisationSettings"
          :merchant-id="activeMerchantId"
          @save:merchant="setMerchantValues"
          v-if="activeTab === 'organization'"
        />
        <Tabs.OrdersTab
          :settings="orderSettings"
          :merchant-id="activeMerchantId"
          @save:merchant="setMerchantValues"
          v-if="activeTab === 'orders'"
        />
        <Tabs.ClientCommunicationsTab
          :settings="clientCommunicationSettings"
          :merchant-id="activeMerchantId"
          @save:merchant="setMerchantValues"
          v-if="activeTab === 'client-communications'"
        />
        <Tabs.NotificationsTab
          :settings="notificationsSettings"
          :merchant-id="activeMerchantId"
          @save:merchant="setMerchantValues"
          v-if="activeTab === 'notifications'"
        />
        <Tabs.ApiIntegrationsTab
          :settings="apiIntegrationSettings"
          :merchant-id="activeMerchantId"
          @save:merchant="setMerchantValues"
          v-if="activeTab === 'api-integrations'"
        />
        <Tabs.TeamsTab
          :numberOfUsers="teamsSettings.numberOfUsers"
          :accountType="teamsSettings.accountType"
          v-if="activeTab === 'team'"
        />
        <Tabs.CustomizeTab
          :settings="customizeSettings"
          :merchant-id="activeMerchantId"
          @save:merchant="setMerchantValues"
          v-if="activeTab === 'customize'"
        />
        <Tabs.BillingFeesTab
          v-if="activeTab === 'billing-fees'"
          :merchant-id="activeMerchantId"
          :settings="billingFeesSettings"
          :user="user"
        />
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import LayupSpinner from '@/components/common/LayupSpinner.vue';
import { NetworkError } from '@/errors';
import { getMerchant } from '@/services/merchants';
import { getPricelistById } from '@/services/priceLists';
import { getUsersByMerchant } from '@/services/user';
import { useMerchantStore } from '@/stores/merchants';
import { useUserStore } from '@/stores/users';
import { addMetric, createException, createWarning } from '@/utils/monitoring.utils';
import { storeToRefs } from 'pinia';
import { onMounted, ref } from 'vue';
import type {
  ModelCustomize,
  ModelFeature,
  ModelMerchantResponse,
  ModelNotification,
  ModelUserResponse,
} from '@layup/api-sdk';
import * as Tabs from './tabs';
import type {
  BillingSettings,
  ClientCommunication,
  CustomCancellationTermRequest,
  NotificationSettings,
  OrderSettings,
  OrganisationSettings,
} from './utils';

const activeTab = ref('organization');
const userStore = useUserStore();
const merchantStore = useMerchantStore();
const { user } = storeToRefs(userStore);
const { activeMerchantId } = storeToRefs(merchantStore);
const isLoading = ref(false);

const tabs: { label: string; value: string }[] = [
  { label: 'Organization', value: 'organization' },
  { label: 'Orders', value: 'orders' },
  { label: 'Client Communications', value: 'client-communications' },
  { label: 'Notifications', value: 'notifications' },
  { label: 'API & Integrations', value: 'api-integrations' },
  { label: 'Team', value: 'team' },
  { label: 'Customize', value: 'customize' },
  { label: 'Billing & Fees', value: 'billing-fees' },
];

const organisationSettings = ref({} as OrganisationSettings);
const orderSettings = ref({} as OrderSettings);
const clientCommunicationSettings = ref({} as ClientCommunication);
const notificationsSettings = ref({} as NotificationSettings);
const apiIntegrationSettings = ref({} as { paymentNotifyUrl?: string; notifyUrl?: string });
const teamsSettings = ref({} as { numberOfUsers: number; accountType: string });
const customizeSettings = ref<{
  customFeatures: ModelCustomize;
  imageUrl: string;
  features: ModelFeature;
  successfulPayment: ModelNotification;
}>();

const billingFeesSettings = ref({} as BillingSettings);
const priceList = ref({} as any);
const users = ref([] as ModelUserResponse[]);

const setActiveTab = (tab: string) => {
  activeTab.value = tab;
};

const setMerchantValues = async (response: ModelMerchantResponse) => {
  users.value = await getUsersByMerchant();

  if (response.pricelistId) {
    priceList.value = await getPricelistById(response.pricelistId);
  }

  merchantStore.merchant = response;

  billingFeesSettings.value = {
    priceList: priceList.value,
    settlementFrequency: response.settlementFrequency!,
    settlementType: response.settlementType!,
    percFee: response.percFee!,
    accountSettings: response.accountSettings!,
    numberOfAddedUsers: users.value.length,
    settlementDay: response.settlementDay!,
    paymentPlans: response.paymentPlans!,
    features: response.features!,
    accountType: response.accountType!,
    name: response.name!,
    supportMobile: response.supportMobile!,
    registeredEntity: response.registeredEntity!,
    bankDetails: response.bankDetails!,
  };

  organisationSettings.value = {
    name: response.name!,
    tradingName: response.tradingName!,
    vatNo: response.vatNo!,
    registeredEntity: response.registeredEntity!,
    supportMobile: response.supportMobile!,
    supportEmail: response.supportEmail!,
    settlementBatchReferencePrefix: response.settlementBatchReferencePrefix!,
    merchantTermsLink: response.merchantTermsLink!,
    billingAddress: response.billingAddress!,
  };

  // If providers aren't set a default value needs to be passed to save correctly
  orderSettings.value = {
    paymentPlans: response.paymentPlans!,
    voucherSettings: {
      ...response.voucherSettings!,
      ...(!response.voucherSettings?.voucherProviders && {
        voucherProviders: [{ name: 'SHOPIFY', apiKey: '', apiUrl: '' }],
      }),
    },
    pricelistId: response.pricelistId!,
    features: response.features!,
    cancellationTerms: response.cancellationTerms! as CustomCancellationTermRequest,
  };

  clientCommunicationSettings.value = response.communication!;

  notificationsSettings.value = {
    settlements: response.settlements!,
    invoices: response.invoices!,
    newOrder: response.newOrder!,
    amendmentOrder: response.amendmentOrder!,
    expiredOrder: response.expiredOrder!,
    suspendedOrder: response.suspendedOrder!,
    cancelledOrder: response.cancelledOrder!,
    cancellationRequest: response.cancellationRequest!,
    completedOrder_75: response.completedOrder_75!,
    completedOrder: response.completedOrder!,
    successfulPayment: response.successfulPayment!,
    failedPayment: response.failedPayment!,
    missedPayment: response.missedPayment!,
    refunds: response.refunds!,
    transfers: response.transfers!,
  };

  // Trimming to remove any whitespace because it breaks the regex validation
  apiIntegrationSettings.value = {
    paymentNotifyUrl: response.paymentNotifyUrl?.trim(),
    notifyUrl: response.notifyUrl?.trim(),
  };

  // STANDARD - 1 USER
  // PRO - 10 USER
  // CUSTOM - ?? USER
  teamsSettings.value = {
    numberOfUsers: response.accountSettings?.numberOfUsers || 1,
    accountType: response.accountType || 'STANDARD',
  };

  customizeSettings.value = {
    customFeatures: { ...response.customFeatures },
    imageUrl: response.imageUrl || '',
    features: {
      enableCustomWidgetLogo: response?.features?.enableCustomWidgetLogo || false,
      enableCustomEmailLogo: response?.features?.enableCustomEmailLogo || false,
      enableDefaultPaymentPlanImage: response?.features?.enableDefaultPaymentPlanImage || false,
    },
    successfulPayment: { ...response.successfulPayment },
  };
  merchantStore.merchant = response;
};

onMounted(async () => {
  try {
    isLoading.value = true;
    const response = await getMerchant(activeMerchantId.value ?? '');

    await setMerchantValues(response);

    addMetric('settings-page', { status: 'success', channel: 'merchant', method: 'getMerchant' });
  } catch (error) {
    if (error instanceof NetworkError) {
      createWarning(error);
      addMetric('settings-page', { status: 'network_error', channel: 'merchant', method: 'getMerchant' });
    } else {
      createException(error);
      addMetric('settings-page', { status: 'error', channel: 'merchant', method: 'getMerchant' });
    }
  } finally {
    isLoading.value = false;
  }
});
</script>
